import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
  Alert,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Checkbox,
  FormGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MonacoEditor from '@monaco-editor/react';
import { toast } from 'react-toastify';
import proxy from '../../../api/axios';
import { getToken } from '../../../common/utils';
import { defaultCode } from '../../../common/constants';
import {
  FUNCTION_PARAM_TYPES,
  DEFAULT_SIGNATURE,
  emptyFunctionTestCase,
  emptyIoTestCase,
  isComplexFunctionType,
  isArgsOutputMode,
  resolveAssertOn,
  normalizeTestCasesForArgsMode,
  functionTypeGroup,
  structTypeHint,
  signatureUsesStructNodes,
} from '../../../common/functionModeTypes';
import { AdminFormSection, ACCENT, adminFieldSx, useAdminPageTheme } from '../../../pages/admin/adminUi';
import { FUNCTION_MODE_PRESETS } from './problemFormConstants';
import FunctionTestCaseEditor from './FunctionTestCaseEditor';
import ProblemFormStubHelpers from './ProblemFormStubHelpers';
import {
  pruneTestCasesForSignature,
  pruneSignatureAssertOn,
} from '../../../common/functionMode';

const PROBLEM_LANGUAGES = ['python', 'javascript', 'java', 'cpp', 'csharp'];

const ModeCard = ({ selected, icon: Icon, title, description, onClick }) => {
  const { border, surface, muted, isDark } = useAdminPageTheme();
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      sx={{
        flex: 1,
        minWidth: 200,
        p: 2,
        borderRadius: 2,
        cursor: 'pointer',
        border: `2px solid ${selected ? ACCENT : border}`,
        bgcolor: selected ? alpha(ACCENT, isDark ? 0.18 : 0.08) : surface,
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover': { borderColor: ACCENT },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Icon sx={{ fontSize: 22, color: selected ? ACCENT : muted }} />
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{title}</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: muted, fontSize: '0.78rem', lineHeight: 1.45 }}>
        {description}
      </Typography>
    </Box>
  );
};

const PaperLikeCase = ({ children, border, surface }) => (
  <Box
    sx={{
      p: 2,
      mb: 1.5,
      borderRadius: 2,
      border: `1px solid ${border}`,
      bgcolor: surface,
      boxShadow: `0 1px 0 ${alpha('#000', 0.04)}`,
    }}
  >
    {children}
  </Box>
);

/**
 * Judging mode + signature + test cases + starter/wrapper code for admin problem form.
 */
const ProblemJudgingPanel = ({
  slug,
  mode,
  onModeChange,
  signature,
  onSignatureChange,
  testCases,
  onTestCasesChange,
  starterCode,
  onStarterCodeChange,
  onStarterCodeEdited,
  onStarterCodeReset,
  wrapperCode,
  onWrapperCodeChange,
  stubHint,
  onStubHintChange,
  starterPreamble,
  onStarterPreambleChange,
  starterOptions,
  onStarterOptionsChange,
  onRegenerateStarters,
}) => {
  const { border, muted, isDark, surface } = useAdminPageTheme();
  const [converting, setConverting] = useState(false);
  const [advancedCases, setAdvancedCases] = useState({});
  const [showCodeEditors, setShowCodeEditors] = useState(false);
  const isFunction = mode === 'function';
  const signatureLooksComplex =
    isFunction &&
    ((signature?.params || []).some((p) => isComplexFunctionType(p.type)) ||
      isComplexFunctionType(signature?.returnType));

  const usesJsonEditor = (index) =>
    advancedCases[index] === true ||
    (advancedCases[index] === undefined && signatureLooksComplex);

  const signatureSyncKey = useMemo(() => {
    if (!isFunction) return '';
    const names = (signature?.params || [])
      .map((p) => String(p.name || '').trim())
      .filter(Boolean)
      .join('|');
    const assertOn = resolveAssertOn(signature).join('|');
    return `${names}::${assertOn}::${signature?.returnType || ''}::${signature?.outputMode || ''}`;
  }, [isFunction, signature]);

  useEffect(() => {
    if (!isFunction || !signatureSyncKey) return;

    const prunedSig = pruneSignatureAssertOn(signature);
    if (prunedSig !== signature) {
      onSignatureChange(prunedSig);
    }

    onTestCasesChange((prev) => {
      let next = pruneTestCasesForSignature(prev, prunedSig);
      if (isArgsOutputMode(prunedSig)) {
        next = normalizeTestCasesForArgsMode(next);
      }
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [signatureSyncKey, isFunction]);

  const handleModeToggle = (next) => {
    onModeChange(next);
    if (next === 'function') {
      onStarterCodeChange?.({});
      if (!testCases.length || !testCases[0]?.args) {
        onTestCasesChange([emptyFunctionTestCase(signature)]);
      }
      if (!signature?.methodName) {
        onSignatureChange({ ...DEFAULT_SIGNATURE });
      }
    }
    if (next === 'io') {
      if (!testCases.length || testCases[0]?.args) {
        onTestCasesChange([emptyIoTestCase()]);
      }
    }
  };

  const handleReturnTypeChange = (value) => {
    const returnType = value || 'int';
    const next = { ...signature, returnType };
    if (String(returnType).toLowerCase() === 'void') {
      next.outputMode = 'args';
      onSignatureChange(next);
      onTestCasesChange((prev) => normalizeTestCasesForArgsMode(prev));
      return;
    }
    onSignatureChange(next);
  };

  const handleOutputModeChange = (value) => {
    if (!value) return;
    const next = { ...signature, outputMode: value };
    onSignatureChange(next);
    if (value === 'args') {
      onTestCasesChange((prev) => normalizeTestCasesForArgsMode(prev));
    }
  };

  const toggleAssertOn = (name, checked) => {
    const current = resolveAssertOn({ ...signature, assertOn: signature?.assertOn || [] });
    const explicit = Array.isArray(signature?.assertOn) && signature.assertOn.length > 0
      ? [...signature.assertOn]
      : (signature?.params || []).map((p) => p.name).filter(Boolean);
    const next = checked
      ? [...new Set([...explicit, name])]
      : explicit.filter((n) => n !== name);
    onSignatureChange({ ...signature, assertOn: next, outputMode: 'args' });
  };

  const applyPreset = (preset) => {
    onSignatureChange({ ...preset.signature });
    onTestCasesChange([preset.sampleCase]);
    toast.success(`Applied "${preset.label}" template`);
  };

  const handleConvertOnServer = async () => {
    if (!slug) {
      toast.info('Save the problem as draft first, then convert mode.');
      return;
    }
    setConverting(true);
    try {
      const target = isFunction ? 'io' : 'function';
      const { data } = await proxy.put(
        `/problems/${slug}/judging-mode`,
        {
          mode: target,
          signature: target === 'function' ? signature : undefined,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      toast.success(data.message || 'Mode updated');
      const p = data.problem;
      onModeChange(p.mode || 'io');
      if (p.signature) onSignatureChange(p.signature);
      onTestCasesChange(p.testCases || []);
      if (p.starterCode) {
        onStarterCodeChange(p.starterCode);
        onStarterCodeReset?.();
      }
      if (p.wrapperCode) onWrapperCodeChange(p.wrapperCode);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const updateParam = (index, field, value) => {
    const params = [...(signature?.params || [])];
    params[index] = { ...params[index], [field]: value };
    onSignatureChange({ ...signature, params });
  };

  const codeMap = isFunction ? starterCode : wrapperCode;
  const setCodeMap = isFunction ? onStarterCodeChange : onWrapperCodeChange;

  const updateCase = (index, nextOrUpdater) => {
    const next = [...testCases];
    next[index] =
      typeof nextOrUpdater === 'function' ? nextOrUpdater(next[index]) : nextOrUpdater;
    onTestCasesChange(next);
  };

  return (
    <>
      <AdminFormSection
        title="How should solutions be judged?"
        description="Pick one mode. You can convert an existing saved problem later."
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <ModeCard
            selected={!isFunction}
            icon={CodeOutlinedIcon}
            title="IO (stdin / stdout)"
            description="Full programs with input/output. Best for contests and classic problems."
            onClick={() => handleModeToggle('io')}
          />
          <ModeCard
            selected={isFunction}
            icon={FunctionsOutlinedIcon}
            title="Function (LeetCode-style)"
            description="Students fill in a method stub. We run hidden test harnesses automatically."
            onClick={() => handleModeToggle('function')}
          />
        </Box>

        {slug && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SwapHorizIcon />}
            disabled={converting}
            onClick={handleConvertOnServer}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {converting ? 'Converting…' : `Convert saved problem → ${isFunction ? 'IO' : 'Function'}`}
          </Button>
        )}

        {isFunction && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Driver code is generated when you save. Students only see the method stub — no main or stdin boilerplate.
          </Alert>
        )}
      </AdminFormSection>

      {isFunction && (
        <AdminFormSection
          title="Method signature"
          description="Start from a template or define your own."
        >
          <Typography variant="caption" sx={{ color: muted, display: 'block', mb: 1 }}>
            Quick templates
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {FUNCTION_MODE_PRESETS.map((preset) => (
              <Button
                key={preset.id}
                size="small"
                variant="outlined"
                onClick={() => applyPreset(preset)}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 99 }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
            <TextField
              label="Class name"
              size="small"
              value={signature?.className || 'Solution'}
              onChange={(e) => onSignatureChange({ ...signature, className: e.target.value })}
              sx={adminFieldSx}
            />
            <TextField
              label="Method name"
              size="small"
              required
              placeholder="twoSum"
              value={signature?.methodName || ''}
              onChange={(e) => onSignatureChange({ ...signature, methodName: e.target.value })}
              sx={adminFieldSx}
            />
            <Autocomplete
              freeSolo
              size="small"
              options={FUNCTION_PARAM_TYPES}
              groupBy={functionTypeGroup}
              value={signature?.returnType || 'int[]'}
              onChange={(_, v) => handleReturnTypeChange(v)}
              onInputChange={(_, v) => handleReturnTypeChange(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Return type"
                  placeholder="ListNode, TreeNode, List<int>…"
                  helperText={structTypeHint(signature?.returnType)}
                  sx={adminFieldSx}
                />
              )}
              sx={adminFieldSx}
            />
          </Box>

          {signatureUsesStructNodes(signature) && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
              <strong>ListNode / TreeNode:</strong> in test cases enter arrays (not objects).
              Linked list <code>1, 2, 3</code> → nodes 1→2→3. Trees use level-order with{' '}
              <code>null</code> for missing children. Class definitions are auto-added to starters.
            </Alert>
          )}

          {String(signature?.returnType || '').toLowerCase() !== 'void' && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="caption" sx={{ color: muted, display: 'block', mb: 0.75 }}>
                What to compare after running
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={signature?.outputMode === 'args' ? 'args' : 'return'}
                onChange={(_, v) => handleOutputModeChange(v)}
              >
                <ToggleButton value="return" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Return value
                </ToggleButton>
                <ToggleButton value="args" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Mutated params
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {isArgsOutputMode(signature) && (signature?.params || []).length > 0 && (
            <Box sx={{ mt: 1.5, mb: 1 }}>
              <Typography variant="caption" sx={{ color: muted, display: 'block', mb: 0.5 }}>
                Assert on parameters (after call)
              </Typography>
              <FormGroup row>
                {(signature?.params || []).map((param) => {
                  const checked = resolveAssertOn(signature).includes(param.name);
                  return (
                    <FormControlLabel
                      key={param.name}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={(e) => toggleAssertOn(param.name, e.target.checked)}
                        />
                      }
                      label={param.name}
                    />
                  );
                })}
              </FormGroup>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
            Parameters
          </Typography>
          {(signature?.params || []).map((param, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField
                label="Name"
                size="small"
                placeholder="nums"
                value={param.name}
                onChange={(e) => updateParam(index, 'name', e.target.value)}
                sx={{ ...adminFieldSx, flex: 1 }}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={FUNCTION_PARAM_TYPES}
                groupBy={functionTypeGroup}
                value={param.type || 'int'}
                onChange={(_, v) => updateParam(index, 'type', v || 'int')}
                onInputChange={(_, v) => updateParam(index, 'type', v || 'int')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type"
                    placeholder="ListNode, int[]…"
                    helperText={structTypeHint(param.type)}
                    sx={{ ...adminFieldSx, minWidth: 200 }}
                  />
                )}
                sx={{ ...adminFieldSx, minWidth: 200 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const params = signature.params.filter((_, i) => i !== index);
                  const nextSig = pruneSignatureAssertOn({ ...signature, params });
                  onSignatureChange(nextSig);
                  onTestCasesChange((prev) => pruneTestCasesForSignature(prev, nextSig));
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              onSignatureChange({
                ...signature,
                params: [...(signature?.params || []), { name: '', type: 'int' }],
              })
            }
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Add parameter
          </Button>
        </AdminFormSection>
      )}

      <AdminFormSection
        title="Test cases"
        description={
          isFunction
            ? 'Fill simple fields per case. Matrices use JSON: [[1,2],[3,4]]. Toggle JSON for maps/sets.'
            : 'Paste stdin and expected stdout for each case.'
        }
      >
        {testCases.map((tc, index) => (
          <PaperLikeCase key={index} border={border} surface={surface}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {tc.isSample ? 'Sample' : 'Hidden'} · Case #{index + 1}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {testCases.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => onTestCasesChange(testCases.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            {isFunction ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={usesJsonEditor(index) ? 'json' : 'simple'}
                    onChange={(_, v) => {
                      if (!v) return;
                      setAdvancedCases({ ...advancedCases, [index]: v === 'json' });
                    }}
                  >
                    <ToggleButton value="simple" sx={{ textTransform: 'none', fontWeight: 700, px: 1.5 }}>
                      Simple
                    </ToggleButton>
                    <ToggleButton value="json" sx={{ textTransform: 'none', fontWeight: 700, px: 1.5 }}>
                      JSON
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {usesJsonEditor(index) ? (
                  <>
                    <TextField
                      label="Args (JSON object)"
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      value={typeof tc.args === 'object' ? JSON.stringify(tc.args, null, 2) : tc.args || '{}'}
                      onChange={(e) => {
                        const next = [...testCases];
                        try {
                          next[index] = { ...next[index], args: JSON.parse(e.target.value) };
                        } catch {
                          next[index] = { ...next[index], args: e.target.value };
                        }
                        onTestCasesChange(next);
                      }}
                      sx={{ ...adminFieldSx, mb: 1.5, fontFamily: 'monospace' }}
                    />
                    <TextField
                      label="Expected (JSON)"
                      fullWidth
                      multiline
                      minRows={1}
                      size="small"
                      value={
                        tc.expected !== undefined && tc.expected !== null
                          ? typeof tc.expected === 'string'
                            ? tc.expected
                            : JSON.stringify(tc.expected)
                          : ''
                      }
                      onChange={(e) => {
                        const next = [...testCases];
                        try {
                          next[index] = { ...next[index], expected: JSON.parse(e.target.value) };
                        } catch {
                          next[index] = { ...next[index], expected: e.target.value };
                        }
                        onTestCasesChange(next);
                      }}
                      sx={{ ...adminFieldSx, mb: 1.5, fontFamily: 'monospace' }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(tc.isSample)}
                          onChange={() => updateCase(index, { ...tc, isSample: !tc.isSample })}
                        />
                      }
                      label="Show as sample on problem page"
                    />
                  </>
                ) : (
                  <FunctionTestCaseEditor
                    signature={signature}
                    testCase={tc}
                    caseKey={String(index)}
                    onChange={(next) => updateCase(index, next)}
                  />
                )}
              </>
            ) : (
              <>
                <TextField
                  label="Input (stdin)"
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  placeholder="Paste what the program reads from stdin"
                  value={tc.input || ''}
                  onChange={(e) => updateCase(index, { ...tc, input: e.target.value })}
                  sx={{ ...adminFieldSx, mb: 1.5 }}
                />
                <TextField
                  label="Expected output (stdout)"
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  value={tc.output || ''}
                  onChange={(e) => updateCase(index, { ...tc, output: e.target.value })}
                  sx={{ ...adminFieldSx, mb: 1.5 }}
                />
                <TextField
                  label="Explanation (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  value={tc.explanation || ''}
                  onChange={(e) => updateCase(index, { ...tc, explanation: e.target.value })}
                  sx={{ ...adminFieldSx, mb: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(tc.isSample)}
                      onChange={() => updateCase(index, { ...tc, isSample: !tc.isSample })}
                    />
                  }
                  label="Show as sample on problem page"
                />
              </>
            )}
          </PaperLikeCase>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            onTestCasesChange([
              ...testCases,
              isFunction ? emptyFunctionTestCase(signature) : emptyIoTestCase(),
            ])
          }
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Add test case
        </Button>
      </AdminFormSection>

      {isFunction && (
        <ProblemFormStubHelpers
          stubHint={stubHint}
          onStubHintChange={onStubHintChange}
          starterPreamble={starterPreamble}
          onStarterPreambleChange={onStarterPreambleChange}
          starterOptions={starterOptions}
          onStarterOptionsChange={onStarterOptionsChange}
          signature={signature}
          onSignatureChange={onSignatureChange}
          onRegenerateStarters={onRegenerateStarters}
        />
      )}

      <AdminFormSection
        title={isFunction ? 'Starter code (optional override)' : 'Wrapper code per language'}
        description={
          isFunction
            ? 'Usually auto-generated on save. Expand only if you need custom stubs.'
            : 'Full program templates shown in the editor when students open the problem.'
        }
      >
        {isFunction && !showCodeEditors ? (
          <Button
            variant="outlined"
            onClick={() => setShowCodeEditors(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Show language editors
          </Button>
        ) : (
          <>
            {isFunction && (
              <Button
                size="small"
                onClick={() => setShowCodeEditors(false)}
                sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700 }}
              >
                Hide editors
              </Button>
            )}
            {PROBLEM_LANGUAGES.map((lang) => (
              <Accordion
                key={lang}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1,
                  border: `1px solid ${border}`,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: ACCENT }}>
                    {lang.toUpperCase()}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ height: 220 }}>
                    <MonacoEditor
                      height="220px"
                      language={lang === 'cpp' ? 'cpp' : lang === 'csharp' ? 'csharp' : lang}
                      theme={isDark ? 'vs-dark' : 'light'}
                      value={codeMap?.[lang] || defaultCode[lang] || ''}
                      onChange={(v) => {
                        setCodeMap({ ...codeMap, [lang]: v });
                        if (isFunction) onStarterCodeEdited?.();
                      }}
                      options={{ minimap: { enabled: false }, fontSize: 13 }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </AdminFormSection>
    </>
  );
};

export default ProblemJudgingPanel;
